
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Property } from '@/types/property';
import { usePropertyFeatureMutations } from './usePropertyFeatureMutations';

interface CreatePropertyData {
  title: string;
  shortName: string;
  shortDescription: string;
  longDescription: string;
  airbnbUrl: string;
  price: number;
  cleaningFee: number;
  propertyFloor?: string;
  rating: number;
  guests: number;
  bedrooms: number;
  bathrooms: number;
  area: number;
  latitude: number;
  longitude: number;
  available: boolean;
  featured: boolean;
  images: string[];
  features?: string[];
  hostId?: string;
  neighborhoodId?: string;
  location?: string;
}

interface UpdatePropertyData extends Partial<CreatePropertyData> {
  id: string;
}

export const usePropertyMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { updatePropertyFeatures } = usePropertyFeatureMutations();

  const generatePropertyId = () => {
    return `prop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const createProperty = useMutation({
    mutationFn: async (data: CreatePropertyData): Promise<Property> => {
      console.log('🏠 Creating new property:', data);

      const propertyId = generatePropertyId();

      const propertyData = {
        id: propertyId,
        title: data.title,
        short_name: data.shortName,
        short_description: data.shortDescription,
        long_description: data.longDescription,
        airbnb_url: data.airbnbUrl || null,
        price: data.price,
        cleaning_fee: data.cleaningFee || 0,
        property_floor: data.propertyFloor || null,
        rating: data.rating,
        guests: data.guests,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        area: data.area || null,
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        available: data.available,
        featured: data.featured,
        images: data.images,
        location: data.location || `${data.title} Location`,
        host_id: data.hostId || null,
        neighborhood_id: data.neighborhoodId || null,
      };

      const { data: property, error } = await supabase
        .from('properties')
        .insert(propertyData)
        .select(`
          *,
          hosts (*),
          neighborhoods (*),
          property_highlights (*),
          reviews (*)
        `)
        .single();

      if (error) {
        console.error('Error creating property:', error);
        throw error;
      }

      console.log('✅ Property created successfully:', property);

      // Handle features using the property_features table
      if (data.features && data.features.length > 0) {
        try {
          await updatePropertyFeatures.mutateAsync({
            propertyId: property.id,
            featureIds: data.features
          });
          console.log('✅ Features added successfully');
        } catch (featuresError) {
          console.error('❌ Error adding features:', featuresError);
          // Don't throw error here to avoid rolling back property creation
        }
      }

      // Transform database response to match Property interface
      const transformedProperty = {
        ...property,
        shortDescription: property.short_description,
        longDescription: property.long_description,
        shortName: property.short_name,
        airbnbUrl: property.airbnb_url,
        cleaningFee: property.cleaning_fee || 0,
        propertyFloor: property.property_floor,
        operationStartedAt: property.operation_started_at || new Date().toISOString(),
        host: property.hosts ? {
          name: property.hosts.name,
          avatar: property.hosts.avatar || '',
          superHost: property.hosts.super_host || false,
          experience: property.hosts.experience || '',
        } : undefined,
        neighborhood: property.neighborhoods ? {
          city: property.neighborhoods.city,
          state: property.neighborhoods.state,
          country: property.neighborhoods.country,
          description: property.neighborhoods.description || '',
          longDescription: property.neighborhoods.long_description || '',
        } : undefined,
        propertyHighlights: property.property_highlights || [],
      } as Property;

      return transformedProperty;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      // Don't show toast here since we'll show it in the component after highlights are created
    },
    onError: (error: any) => {
      console.error('❌ Error creating property:', error);
      toast({
        title: "Error",
        description: `Error al crear la propiedad: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateProperty = useMutation({
    mutationFn: async (data: UpdatePropertyData): Promise<Property> => {
      console.log('🔄 Updating property:', data.id, 'with data:', data);
      
      // Build update data object
      const updateData: any = {};
      
      // Only include fields that are actually being updated
      if (data.title !== undefined) updateData.title = data.title;
      if (data.shortName !== undefined) updateData.short_name = data.shortName;
      if (data.shortDescription !== undefined) updateData.short_description = data.shortDescription;
      if (data.longDescription !== undefined) updateData.long_description = data.longDescription;
      if (data.airbnbUrl !== undefined) updateData.airbnb_url = data.airbnbUrl;
      if (data.price !== undefined) updateData.price = data.price;
      if (data.cleaningFee !== undefined) updateData.cleaning_fee = data.cleaningFee;
      if (data.propertyFloor !== undefined) updateData.property_floor = data.propertyFloor;
      if (data.rating !== undefined) updateData.rating = data.rating;
      if (data.guests !== undefined) updateData.guests = data.guests;
      if (data.bedrooms !== undefined) updateData.bedrooms = data.bedrooms;
      if (data.bathrooms !== undefined) updateData.bathrooms = data.bathrooms;
      if (data.area !== undefined) updateData.area = data.area;
      if (data.latitude !== undefined) updateData.latitude = data.latitude;
      if (data.longitude !== undefined) updateData.longitude = data.longitude;
      if (data.available !== undefined) updateData.available = data.available;
      if (data.featured !== undefined) updateData.featured = data.featured;
      
      // Handle UUID fields - convert empty strings to null
      if (data.hostId !== undefined) {
        updateData.host_id = data.hostId && data.hostId.trim() !== '' ? data.hostId : null;
      }
      if (data.neighborhoodId !== undefined) {
        updateData.neighborhood_id = data.neighborhoodId && data.neighborhoodId.trim() !== '' ? data.neighborhoodId : null;
      }
      
      if (data.images && data.images.length > 0) updateData.images = data.images;

      // Force update timestamp
      updateData.updated_at = new Date().toISOString();

      console.log('📤 Sending update to Supabase:', updateData);

      // Execute the update
      const { error: updateError } = await supabase
        .from('properties')
        .update(updateData)
        .eq('id', data.id);

      if (updateError) {
        console.error('❌ Update failed:', updateError);
        throw new Error(`Error al actualizar la propiedad: ${updateError.message}`);
      }

      console.log('✅ Update successful, fetching updated data...');

      // Fetch the updated property with all related data
      const { data: updatedProperty, error: fetchError } = await supabase
        .from('properties')
        .select(`
          *,
          hosts (*),
          neighborhoods (*),
          property_highlights (*)
        `)
        .eq('id', data.id)
        .single();

      if (fetchError || !updatedProperty) {
        console.error('❌ Error fetching updated property:', fetchError);
        throw new Error('No se pudo obtener la propiedad actualizada');
      }

      console.log('📊 Updated property fetched:', updatedProperty);

      // Handle features if provided
      if (data.features !== undefined) {
        console.log('🏷️ Updating features...');
        try {
          await updatePropertyFeatures.mutateAsync({
            propertyId: data.id,
            featureIds: data.features
          });
          console.log('✅ Features updated');
        } catch (featuresError) {
          console.error('❌ Features update failed:', featuresError);
        }
      }

      // Transform and return the result
      const transformedProperty = {
        ...updatedProperty,
        shortDescription: updatedProperty.short_description,
        longDescription: updatedProperty.long_description,
        shortName: updatedProperty.short_name,
        airbnbUrl: updatedProperty.airbnb_url,
        cleaningFee: updatedProperty.cleaning_fee || 0,
        propertyFloor: updatedProperty.property_floor,
        operationStartedAt: updatedProperty.operation_started_at || new Date().toISOString(),
        host: updatedProperty.hosts ? {
          name: updatedProperty.hosts.name,
          avatar: updatedProperty.hosts.avatar || '',
          superHost: updatedProperty.hosts.super_host || false,
          experience: updatedProperty.hosts.experience || '',
        } : undefined,
        neighborhood: updatedProperty.neighborhoods ? {
          city: updatedProperty.neighborhoods.city,
          state: updatedProperty.neighborhoods.state,
          country: updatedProperty.neighborhoods.country,
          description: updatedProperty.neighborhoods.description || '',
          longDescription: updatedProperty.neighborhoods.long_description || '',
        } : undefined,
        propertyHighlights: updatedProperty.property_highlights || [],
      } as Property;

      console.log('🎉 Property update completed successfully');
      return transformedProperty;
    },
    onSuccess: (_, variables) => {
      console.log('🔄 Invalidating queries after successful update');
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['property', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['property-features', variables.id] });
      
      toast({
        title: "Propiedad actualizada",
        description: "La propiedad ha sido actualizada exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('❌ Property update failed:', error);
      toast({
        title: "Error",
        description: `Error al actualizar la propiedad: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const deleteProperty = useMutation({
    mutationFn: async (propertyId: string): Promise<void> => {
      console.log('🗑️ Deleting property:', propertyId);
      
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);

      if (error) {
        console.error('Error deleting property:', error);
        throw error;
      }

      console.log('✅ Property deleted successfully');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      toast({
        title: "Propiedad eliminada",
        description: "La propiedad ha sido eliminada exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('❌ Error deleting property:', error);
      toast({
        title: "Error",
        description: `Error al eliminar la propiedad: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    createProperty,
    updateProperty,
    deleteProperty,
  };
};
